<template>
  <loading-element v-if="isLoadingRecipes" />

  <div class="main bg-primary" v-else>
    <div class="rowSpaceBetween fullWidth mb-5">
      <TopDeck
        v-if="authStore.userInfos"
        firstElement="Mon"
        secondElement="menu"
        :coloredSecond="true"
      />
      <div class="rowCustom">
        <ThemeToggle />
        <AccountToggle />
      </div>
    </div>

    <!-- CONTEXTE NUTRITIONNEL -->
    <div class="mn-context-grid mb-6">
      <div class="mn-context-card">
        <p class="mn-context-label">Objectif</p>
        <p class="mn-context-value">{{ $t(weightObjective) }}</p>
      </div>
      <div class="mn-context-card">
        <p class="mn-context-label">Calories</p>
        <p class="mn-context-value">{{ targetCalories.toFixed(0) }} kcal/j</p>
      </div>
      <div class="mn-context-card">
        <p class="mn-context-label">Régime</p>
        <p class="mn-context-value">{{ userConstraints.diet.length ? userConstraints.diet.map((d) => $t(d)).join(", ") : "Aucun" }}</p>
      </div>
      <div class="mn-context-card">
        <p class="mn-context-label">Repas/jour</p>
        <p class="mn-context-value">{{ userConstraints.takenMealsObjectives.length }}</p>
      </div>
    </div>

    <!-- MENU SEMAINE -->
    <div v-if="displayedWeeklyData.daily && displayedWeeklyData.daily.length" class="mt-2">
      <div class="weekHeader d-flex justify-space-between align-start flex-wrap mb-5" style="gap: 12px;">
        <div>
          <p class="weekRangeTitle mb-0">{{ weekRangeLabel }}</p>
        </div>
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <v-btn
            color="secondary"
            prepend-icon="mdi-refresh"
            elevation="0"
            rounded="lg"
            size="small"
            @click="rerollMenu"
          >
            Régénérer
          </v-btn>
          <v-btn
            color="white"
            prepend-icon="mdi-printer"
            elevation="0"
            rounded="lg"
            size="small"
            @click="printMenu"
            :loading="isPrinting"
          >
            Imprimer
          </v-btn>
          <v-btn
            v-if="isSavingMenu"
            color="accent"
            elevation="0"
            rounded="lg"
            size="small"
            :loading="true"
          >
            Sauvegarde...
          </v-btn>
        </div>
      </div>

      <!-- Bandeau d'options -->
      <div class="optionsRow mb-5">
        <v-tooltip location="bottom" max-width="260">
          <template #activator="{ props: tooltipProps }">
            <button
              v-bind="tooltipProps"
              class="optionPill"
              :class="{ 'optionPill--active': diverse }"
              @click="diverse = !diverse"
            >
              <v-icon size="14">mdi-leaf</v-icon>
              Menus diversifiés
            </button>
          </template>
          Évite qu'une même recette apparaisse plusieurs fois dans la semaine. Chaque recette n'est proposée qu'une seule fois sur les 7 jours.
        </v-tooltip>

        <v-tooltip location="bottom" max-width="260">
          <template #activator="{ props: tooltipProps }">
            <button
              v-bind="tooltipProps"
              class="optionPill"
              :class="{ 'optionPill--active': sameLunchDinnerDays.length > 0 }"
              @click="toggleSameLunchDinnerAll"
            >
              <v-icon size="14">mdi-repeat</v-icon>
              Même plat midi et soir
            </button>
          </template>
          Active cette option jour par jour ci-dessous pour cuisiner moins. Cliquer ici l'active ou la désactive pour toute la semaine.
        </v-tooltip>

        <span v-if="savedAt" class="savedNotice">
          <v-icon size="13">mdi-clock-outline</v-icon>
          Sauvegardé {{ savedAtLabel }}
        </span>
      </div>

      <v-alert
        v-if="showExpiredNotice"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        Votre menu sauvegardé a plus d'une semaine. Merci de générer et sauvegarder un nouveau menu.
      </v-alert>

      <div ref="menuSectionRef">
        <!-- Jour sélectionné, en grand -->
        <div v-if="displayedWeeklyData.daily[selectedDayIndex]" class="largeDaySection">
          <DayMenuCard
            :day="displayedWeeklyData.daily[selectedDayIndex]"
            :label="DAY_FULL_LABELS[selectedDayIndex]"
            :is-today="selectedDayIndex === todayIndex"
            :is-same-lunch-dinner="sameLunchDinnerDays.includes(selectedDayIndex)"
            :large="true"
            :protein-needs="proteinNeeds"
            :fat-needs="fatNeeds"
            :carb-needs="carbNeeds"
            @toggle-same-day="toggleSameDay(selectedDayIndex)"
            @open-recipe="openRecipePopup"
          />
        </div>

        <!-- Les 6 autres jours, grille 3x2 sans trou -->
        <div class="gridDays">
          <DayMenuCard
            v-for="idx in otherDaysIndices"
            :key="idx"
            :day="displayedWeeklyData.daily[idx]"
            :label="DAY_FULL_LABELS[idx]"
            :is-today="idx === todayIndex"
            :is-same-lunch-dinner="sameLunchDinnerDays.includes(idx)"
            :large="false"
            :protein-needs="proteinNeeds"
            :fat-needs="fatNeeds"
            :carb-needs="carbNeeds"
            @select="selectedDayIndex = idx"
            @toggle-same-day="toggleSameDay(idx)"
            @open-recipe="openRecipePopup"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- POPUP DÉTAIL RECETTE -->
  <v-dialog v-model="recipePopupOpen" max-width="680" scrollable>
    <v-card class="recipePopup" rounded="xl" elevation="0">
      <!-- Header -->
      <div class="rp-header">
        <div class="rp-header-img">
          <v-img
            v-if="selectedRecipe?.image || selectedRecipe?.imageUrl"
            :src="selectedRecipe?.image || selectedRecipe?.imageUrl"
            cover
            class="rp-hero-img"
          />
          <div v-else class="rp-hero-placeholder">
            <v-icon size="48" style="opacity:0.2">mdi-food-variant</v-icon>
          </div>
          <div class="rp-header-overlay" />
          <button class="rp-close-btn" @click="recipePopupOpen = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
          <div class="rp-header-title">
            <span v-if="selectedRecipe?.category" class="rp-eyebrow">{{ $t(selectedRecipe.category) }}</span>
            <h2 class="rp-title">{{ selectedRecipe?.title || 'Sans titre' }}</h2>
          </div>
        </div>
      </div>

      <!-- Macros -->
      <div class="rp-macros pa-4">
        <div class="rp-macro-item">
          <span class="rp-macro-val">{{ Math.round((selectedRecipe?.totals?.kcal ?? 0) / (selectedRecipe?.servings || 1)) }}</span>
          <span class="rp-macro-unit">kcal</span>
        </div>
        <div class="rp-macro-sep" />
        <div class="rp-macro-item">
          <span class="rp-macro-val">{{ ((selectedRecipe?.totals?.protein ?? 0) / (selectedRecipe?.servings || 1)).toFixed(0) }}</span>
          <span class="rp-macro-unit">g prot.</span>
        </div>
        <div class="rp-macro-sep" />
        <div class="rp-macro-item">
          <span class="rp-macro-val">{{ ((selectedRecipe?.totals?.carbs ?? 0) / (selectedRecipe?.servings || 1)).toFixed(0) }}</span>
          <span class="rp-macro-unit">g gluc.</span>
        </div>
        <div class="rp-macro-sep" />
        <div class="rp-macro-item">
          <span class="rp-macro-val">{{ ((selectedRecipe?.totals?.fat ?? 0) / (selectedRecipe?.servings || 1)).toFixed(0) }}</span>
          <span class="rp-macro-unit">g lip.</span>
        </div>
        <div v-if="selectedRecipe?.servings" class="rp-portions">
          <v-icon size="13" color="accent" class="mr-1">mdi-account-multiple-outline</v-icon>
          {{ selectedRecipe.servings }} portion{{ selectedRecipe.servings > 1 ? 's' : '' }}
        </div>
      </div>

      <!-- Ingrédients -->
      <div v-if="selectedRecipe?.ingredients?.length" class="rp-section px-4 pb-3">
        <p class="rp-section-title">Ingrédients <span class="rp-section-count">{{ selectedRecipe.ingredients.length }}</span></p>
        <div class="rp-ing-list">
          <div v-for="(ing, i) in selectedRecipe.ingredients" :key="i" class="rp-ing-item">
            <div class="rp-ing-img-wrap">
              <img v-if="ing.productImage" :src="ing.productImage" class="rp-ing-img" :alt="ing.name" />
              <v-icon v-else size="16" style="opacity:0.3">mdi-food-variant</v-icon>
            </div>
            <span class="rp-ing-name">{{ ing.name || ing.productName }}</span>
            <span class="rp-ing-qty">{{ ing.grams }}&thinsp;g</span>
          </div>
        </div>
      </div>

      <!-- Étapes -->
      <div v-if="selectedRecipe?.steps?.length" class="rp-section px-4 pb-4">
        <p class="rp-section-title">Préparation</p>
        <div class="rp-steps">
          <div v-for="(step, i) in selectedRecipe.steps" :key="i" class="rp-step">
            <div class="rp-step-num">{{ i + 1 }}</div>
            <div class="rp-step-text" v-html="step" />
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>

  <v-snackbar
    v-if="errorMessage"
    v-model="errorMessage"
    color="error"
    top
    right
  >
    {{ errorMessage }}
  </v-snackbar>
  <v-snackbar v-model="menuSnack.show" color="success" timeout="2500" top right>
    {{ menuSnack.text }}
  </v-snackbar>
</template>

<script>
import { computed, ref, onMounted, watch, nextTick } from "vue";
import LoadingElement from "../Animations/LoadingElement.vue";
import TopDeck from "../Utils/TopDeck.vue";
import { useAuthStore } from "@/store/auth_store";
import {
  getBaseMetabolicRate,
  getDailyNeeds,
  getTargetCalories,
  calculateMacros,
} from "@/service/health_service";
import { getUserRecipes } from "@/service/recipe_service";
import { generateMenu, generateWeeklyMenu } from "@/service/menu_service";
import { getUserWeeklyMenu, saveUserWeeklyMenu } from "@/service/menu_storage_service";
import { syncMenuShoppingList } from "@/service/shopping_list_service";
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import AccountToggle from "@/components/Utils/AccountToggle.vue";
import DayMenuCard from "./DayMenuCard.vue";
import html2canvas from "html2canvas";

export default {
  name: "MenuPage",
  components: { LoadingElement, TopDeck, ThemeToggle, AccountToggle, DayMenuCard },
  setup() {
    const authStore = useAuthStore();
    const errorMessage = ref("");
    const recipes = ref([]);
    const isLoadingRecipes = ref(false);
    const menuSectionRef = ref(null);
    const isPrinting = ref(false);
    const isSavingMenu = ref(false);
    const menuSnack = ref({ show: false, text: "" });
    const savedMenu = ref(null);
    const savedAt = ref(null);
    const useSavedMenu = ref(false);

    const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

    const diverse = ref(true);
    const sameLunchDinnerDays = ref([]);
    // Empêche le watcher de sameLunchDinnerDays de désactiver useSavedMenu
    // pendant qu'on restaure sa valeur depuis la sauvegarde (ce n'est pas une
    // vraie modification de l'utilisateur).
    const isRestoringSavedOptions = ref(false);

    onMounted(() => {
      watch(
        () => authStore.userInfos,
        async () => {
          if (!authStore?.userid) return;
          try {
            isLoadingRecipes.value = true;
            const data = await getUserRecipes(authStore.userid);
            recipes.value = Object.entries(data).map(([id, value]) => ({
              id,
              ...value,
            }));
          } catch (error) {
            errorMessage.value =
              "Erreur lors du chargement des recettes utilisateur.";
            console.error(error);
          } finally {
            isLoadingRecipes.value = false;
          }

          try {
            const saved = await getUserWeeklyMenu(authStore.userid);
            savedMenu.value = saved?.weeklyMenu || null;
            savedAt.value = saved?.savedAt || null;
            const isFresh = saved?.savedAt && Date.now() - saved.savedAt < WEEK_MS;
            useSavedMenu.value = !!saved?.weeklyMenu && isFresh;

            // Restaure les options ("Menus diversifiés", "Déjeuner = Dîner")
            // telles qu'elles étaient au moment de la sauvegarde, pour que
            // les sélections survivent à un changement de page.
            isRestoringSavedOptions.value = true;
            if (typeof saved?.diverse === "boolean") {
              diverse.value = saved.diverse;
            }
            sameLunchDinnerDays.value = Array.isArray(saved?.sameLunchDinnerDays)
              ? [...saved.sameLunchDinnerDays]
              : [];
            await nextTick();
            isRestoringSavedOptions.value = false;

            // Génère un menu seulement s'il n'y a pas de sauvegarde fraîche à afficher.
            // Si un menu sauvegardé frais existe, on l'affiche tel quel sans régénérer.
            if (!useSavedMenu.value) {
              generatedWeeklyData.value = generateWeeklyMenu(
                recipes.value,
                userConstraints.value,
                {
                  diverse: diverse.value,
                  sameLunchDinnerDays: sameLunchDinnerDays.value,
                }
              );
            }
          } catch (error) {
            console.error("Erreur chargement menu sauvegardé:", error);
          }
        },
        { immediate: true }
      );
    });

    // Données du store
    const weight = computed(() => Number(authStore.userInfos?.actualWeight) || 70);
    const height = computed(() => Number(authStore.userInfos?.actualSize) || 175);
    const age = computed(() => authStore.userInfos?.age || 30);
    const sex = computed(() => authStore.userInfos?.sex || "male");
    const activityLevel = computed(() => authStore.userInfos?.activityLevel || "sedentary");
    const weightObjective = computed(() => authStore.userInfos?.weightObjective || "maintainWeight");
    const nap = computed(() => authStore.userInfos?.NAP || 1.5);

    //Cooking data
    const userConstraints = computed(() => ({
      targetCalories: targetCalories.value,
      takenMealsObjectives: authStore.userInfos?.takenMealsObjectives || [
        "breakfast",
        "lunch",
        "dinner",
      ],
      allergies: authStore.userInfos?.allergies || [],
      intolerances: authStore.userInfos?.intolerances || [],
      diet: authStore.userInfos?.diet || [],
      bannedIngredients: authStore.userInfos?.bannedIngredients || [],
      averageCookingTime:
        authStore.userInfos?.averageCookingTime || "15To30Minutes",
      cookingSkills: authStore.userInfos?.cookingSkills || null,
      equipement: authStore.userInfos?.equipement || [],
    }));

    // Jour affiché en grand, "jour 1" (lundi) par défaut
    const selectedDayIndex = ref(0);
    const otherDaysIndices = computed(() => {
      const total = displayedWeeklyData.value?.daily?.length || 0;
      return Array.from({ length: total }, (_, i) => i).filter((i) => i !== selectedDayIndex.value);
    });
    const toggleSameDay = (idx) => {
      sameLunchDinnerDays.value.includes(idx)
        ? sameLunchDinnerDays.value.splice(sameLunchDinnerDays.value.indexOf(idx), 1)
        : sameLunchDinnerDays.value.push(idx);
    };

    // Dès qu'on change un jour "même midi & soir", on quitte le menu sauvegardé
    // (sauf si ce changement vient de la restauration automatique au chargement)
    watch(sameLunchDinnerDays, () => {
      if (isRestoringSavedOptions.value) return;
      useSavedMenu.value = false;
    }, { deep: true });

    // Popup recette
    const recipePopupOpen = ref(false);
    const selectedRecipe = ref(null);
    const openRecipePopup = (recipe) => {
      selectedRecipe.value = recipe;
      recipePopupOpen.value = true;
    };

    const menuData = computed(() =>
      generateMenu(recipes.value, userConstraints.value)
    );

    // Remplacé en ref : le menu ne se régénère QUE sur rerollMenu explicite,
    // jamais automatiquement au remontage du composant.
    const generatedWeeklyData = ref(null);

    const hasSavedMenu = computed(() => !!savedMenu.value);
    const isSavedFresh = computed(
      () => !!savedAt.value && Date.now() - savedAt.value < WEEK_MS
    );
    const showExpiredNotice = computed(
      () => hasSavedMenu.value && !isSavedFresh.value
    );
    const displayedWeeklyData = computed(() => {
      if (useSavedMenu.value && hasSavedMenu.value && isSavedFresh.value) {
        return savedMenu.value ?? { daily: [] };
      }
      return generatedWeeklyData.value ?? { daily: [] };
    });

    const showSaveButton = computed(() => !useSavedMenu.value);
    const saveButtonLabel = computed(() =>
      hasSavedMenu.value && isSavedFresh.value ? "Remplacer" : "Sauvegarder"
    );

    // Calculs nutritionnels
    const tmb = computed(() => getBaseMetabolicRate(age.value, height.value, weight.value, sex.value));
    const dailyNeeds = computed(() => getDailyNeeds(tmb.value, nap.value));
    const targetCalories = computed(() =>
      getTargetCalories(dailyNeeds.value, weightObjective.value)
    );

    const macros = computed(() =>
      calculateMacros(
        weight.value,
        activityLevel.value,
        weightObjective.value,
        targetCalories.value
      )
    );

    const proteinNeeds = computed(() => macros.value.proteinNeeds);
    const fatNeeds = computed(() => macros.value.fatNeeds);
    const carbNeeds = computed(() => macros.value.carbNeeds);

    // Jour courant (0 = lundi ... 6 = dimanche), aligné sur DAY_LABELS/DAY_FULL_LABELS
    const todayIndex = computed(() => {
      const jsDay = new Date().getDay(); // 0 = dimanche
      return jsDay === 0 ? 6 : jsDay - 1;
    });

    const weekRangeLabel = computed(() => {
      const now = new Date();
      const monday = new Date(now);
      const offset = now.getDay() === 0 ? -6 : 1 - now.getDay();
      monday.setDate(now.getDate() + offset);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const fmt = (d) => d.toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
      return `Semaine du ${fmt(monday)} au ${fmt(sunday)}`;
    });

    const savedAtLabel = computed(() => {
      if (!savedAt.value) return "";
      const diffMs = Date.now() - savedAt.value;
      const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
      if (diffDays <= 0) return "aujourd'hui";
      if (diffDays === 1) return "hier";
      return `il y a ${diffDays} jours`;
    });

    const toggleSameLunchDinnerAll = () => {
      if (sameLunchDinnerDays.value.length > 0) {
        sameLunchDinnerDays.value = [];
      } else {
        sameLunchDinnerDays.value = [0, 1, 2, 3, 4, 5, 6];
      }
    };

    const printMenu = async () => {
      if (!menuSectionRef.value) {
        console.error("Menu section ref non disponible");
        return;
      }

      isPrinting.value = true;

      try {
        const canvas = await html2canvas(menuSectionRef.value, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
        });

        const imgData = canvas.toDataURL("image/png");
        const printWindow = window.open("", "_blank", "width=900,height=1200");
        
        printWindow.document.write(`
          <html>
            <head>
              <title>Menu de la semaine</title>
              <style>
                body { margin: 0; padding: 0; }
                img { max-width: 100%; height: auto; display: block; }
                @media print {
                  img { page-break-inside: avoid; }
                }
              </style>
            </head>
            <body>
              <img src="${imgData}" />
            </body>
          </html>
        `);

        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        };
      } catch (error) {
        console.error("Erreur lors de l'impression:", error);
        errorMessage.value = "Erreur lors de la génération de l'impression";
      } finally {
        isPrinting.value = false;
      }
    };

    const saveMenu = async () => {
      if (!authStore.userid) {
        errorMessage.value = "Utilisateur non connecté.";
        return;
      }
      const menuToSave = generatedWeeklyData.value;
      if (!menuToSave?.daily?.length) {
        errorMessage.value = "Aucun menu à sauvegarder.";
        return;
      }

      isSavingMenu.value = true;
      try {
        const meta = { diverse: diverse.value, sameLunchDinnerDays: sameLunchDinnerDays.value };
        const res = await saveUserWeeklyMenu(authStore.userid, menuToSave, meta);
        savedMenu.value = menuToSave;
        savedAt.value = res?.savedAt || Date.now();
        useSavedMenu.value = true;
        menuSnack.value = { show: true, text: "Menu sauvegardé ✅" };
      } catch (error) {
        console.error("Erreur sauvegarde menu:", error);
        errorMessage.value = "Erreur lors de la sauvegarde du menu.";
      } finally {
        isSavingMenu.value = false;
      }
    };

    const rerollMenu = async () => {
      useSavedMenu.value = false;
      generatedWeeklyData.value = generateWeeklyMenu(
        recipes.value,
        userConstraints.value,
        {
          diverse: diverse.value,
          sameLunchDinnerDays: sameLunchDinnerDays.value,
        }
      );
      await nextTick();
      await saveMenu();
      // Régénère automatiquement la liste de courses depuis le nouveau menu
      if (authStore.userid && generatedWeeklyData.value) {
        syncMenuShoppingList(authStore.userid, generatedWeeklyData.value).catch((e) =>
          console.error("Erreur sync liste de courses:", e)
        );
      }
    };

    return {
      //User data
      authStore,
      errorMessage,
      recipes,
      isLoadingRecipes,
      weightObjective,
      targetCalories,
      proteinNeeds,
      fatNeeds,
      carbNeeds,
      //Cooking data,
      userConstraints,
      menuData,
      generatedWeeklyData,
      displayedWeeklyData,
      showSaveButton,
      saveButtonLabel,
      showExpiredNotice,
      diverse,
      //Print
      menuSectionRef,
      isPrinting,
      printMenu,
      rerollMenu,
      saveMenu,
      isSavingMenu,
      menuSnack,
      sameLunchDinnerDays,
      DAY_LABELS: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
      DAY_FULL_LABELS: ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'],
      todayIndex,
      weekRangeLabel,
      savedAt,
      savedAtLabel,
      toggleSameLunchDinnerAll,
      selectedDayIndex,
      otherDaysIndices,
      toggleSameDay,
      recipePopupOpen,
      selectedRecipe,
      openRecipePopup,
    };
  },
};
</script>

<style scoped>
/* ── 4 cartes contexte violettes ── */
.mn-context-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  background: linear-gradient(135deg, rgb(var(--v-theme-accent)), rgba(180,140,255,0.7));
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 24px;
}
@media (max-width: 600px) {
  .mn-context-grid { grid-template-columns: repeat(2, 1fr); }
}
.mn-context-card {
  padding: 16px 18px;
  background: transparent;
  border-right: 1px solid rgba(255,255,255,0.2);
}
.mn-context-card:last-child {
  border-right: none;
}
.mn-context-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255,255,255,0.75);
  margin: 0 0 6px;
}
.mn-context-value {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Header semaine ── */
.weekRangeTitle {
  font-size: 1.35rem;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  letter-spacing: -0.01em;
}

.weekSubtitle {
  font-size: 0.85rem;
  color: rgba(var(--v-theme-text), 0.6);
}

/* ── Bandeau d'options ── */
.optionsRow {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.optionPill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid rgba(var(--v-theme-text), 0.14);
  background: rgba(var(--v-theme-tertiary), 0.6);
  color: rgba(var(--v-theme-text), 0.65);
  transition: all 0.15s;
}

.optionPill--active {
  background: rgba(var(--v-theme-accent), 0.16);
  color: rgb(var(--v-theme-accent));
  border-color: rgba(var(--v-theme-accent), 0.4);
}

.savedNotice {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  color: rgba(var(--v-theme-text), 0.45);
}

/* ── Jour sélectionné, en grand, pleine largeur ── */
.largeDaySection {
  margin-bottom: 0.9rem;
}

/* ── Grille des 6 autres jours : 3 colonnes x 2 lignes, pas de trou ── */
.gridDays {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
}

@media (max-width: 720px) {
  .gridDays {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 480px) {
  .gridDays {
    grid-template-columns: 1fr;
  }
}

.rowCustom {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

/* ── Popup recette ── */
.recipePopup {
  background: rgb(var(--v-theme-secondary)) !important;
  overflow: hidden;
}
.rp-header {
  position: relative;
}
.rp-header-img {
  position: relative;
  height: 200px;
  overflow: hidden;
  background: rgba(var(--v-theme-text), 0.06);
}
.rp-hero-img {
  width: 100%;
  height: 100%;
}
.rp-hero-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rp-header-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 55%);
}
.rp-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.4);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
  z-index: 2;
}
.rp-close-btn:hover { background: rgba(0,0,0,0.6); }
.rp-header-title {
  position: absolute;
  bottom: 14px;
  left: 16px;
  right: 52px;
  z-index: 2;
}
.rp-eyebrow {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgb(var(--v-theme-accent));
  background: rgba(var(--v-theme-accent), 0.18);
  padding: 2px 8px;
  border-radius: 999px;
  margin-bottom: 4px;
}
.rp-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.2;
  text-shadow: 0 1px 4px rgba(0,0,0,0.4);
}
.rp-macros {
  display: flex;
  align-items: center;
  gap: 0;
  background: rgba(var(--v-theme-tertiary), 0.8);
  border-bottom: 1px solid rgba(var(--v-theme-text), 0.07);
  flex-wrap: wrap;
}
.rp-macro-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px;
}
.rp-macro-val {
  font-size: 18px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  line-height: 1;
}
.rp-macro-unit {
  font-size: 11px;
  color: rgba(var(--v-theme-text), 0.5);
  margin-top: 2px;
}
.rp-macro-sep {
  width: 1px;
  height: 28px;
  background: rgba(var(--v-theme-text), 0.1);
}
.rp-portions {
  margin-left: auto;
  font-size: 12px;
  color: rgba(var(--v-theme-text), 0.6);
  display: flex;
  align-items: center;
  padding-right: 4px;
}
.rp-section {
  border-top: 1px solid rgba(var(--v-theme-text), 0.07);
}
.rp-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(var(--v-theme-text), 0.5);
  margin: 14px 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.rp-section-count {
  background: rgba(var(--v-theme-accent), 0.15);
  color: rgb(var(--v-theme-accent));
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 11px;
}
.rp-ing-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rp-ing-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 10px;
  background: rgba(var(--v-theme-text), 0.03);
  border: 1px solid rgba(var(--v-theme-text), 0.06);
}
.rp-ing-img-wrap {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(var(--v-theme-text), 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
}
.rp-ing-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.rp-ing-name {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--v-theme-text));
}
.rp-ing-qty {
  font-size: 12px;
  font-weight: 600;
  color: rgba(var(--v-theme-text), 0.5);
  background: rgba(var(--v-theme-text), 0.06);
  padding: 2px 8px;
  border-radius: 999px;
}
.rp-steps {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rp-step {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.rp-step-num {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgb(var(--v-theme-accent));
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.rp-step-text {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(var(--v-theme-text), 0.85);
}
</style>
